import type { Provider } from '@supabase/gotrue-js';
import getEnv from '~/core/get-env';
import { StripeCheckoutDisplayMode } from '~/lib/stripe/types';

const env = getEnv() ?? {};
const production = env.NODE_ENV === 'production';

enum Themes {
  Light = 'light',
  Dark = 'dark',
}

const configuration = {
  site: {
    name: 'FaithGPT',
    description:
      'FaithGPT is an AI powered collection of Bible study tools designed to help you study, interact & understand the Bible better.',
    description2:
      'It is an advanced AI platform designed to assist users in studying the Bible and delving deeper into the Christian faith. It offers a wide range of features, including interactive chats with biblical characters, contextual Bible verse analysis, and more.',
    themeColor: '#ffffff',
    themeColorDark: '#0a0a0a',
    siteUrl: env.SITE_URL,
    siteName: 'FaithGPT | Bringing the Bible to digital Life with A.I.',
    tagline: 'Bringing the Bible to digital Life with A.I.',
    twitterHandle: 'faithgpt',
    githubHandle: '',
    language: 'en',
    convertKitFormId: '',
    locale: env.DEFAULT_LOCALE,
  },
  auth: {
    // ensure this is the same as your Supabase project
    requireEmailConfirmation: true,
    // NB: Enable the providers below in the Supabase Console
    // in your production project
    providers: {
      emailPassword: true,
      phoneNumber: false,
      emailLink: false,
      oAuth: ['google'] as Provider[],
    },
  },
  production,
  environment: env.ENVIRONMENT,
  theme: Themes.Light,
  features: {
    enableThemeSwitcher: true,
    enableAccountDeletion: getBoolean(env.ENABLE_ACCOUNT_DELETION, true),
    enableOrganizationDeletion: getBoolean(
      env.ENABLE_ORGANIZATION_DELETION,
      true,
    ),
  },
  paths: {
    pray: '/pray',
    sermonAnalysis: '/sermon-analysis',
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
    signInMfa: '/auth/verify',
    signInFromLink: '/auth/link',
    callback: '/auth/callback',
    onboarding: `/onboarding`,
    appHome: '/workspaces',
    settings: {
      profile: '/settings/profile',
      organization: '/settings/organization',
      subscription: '/settings/subscription',
      pray: '/pray',
      sermonAnalysis: '/sermon-analysis',
      authentication: '/settings/profile/authentication',
      email: '/settings/profile/email',
      password: '/settings/profile/password',
    },
    api: {
      checkout: `/resources/stripe/checkout`,
      billingPortal: `/resources/stripe/portal`,
      organizations: {
        create: `/resources/organizations/create`,
        transferOwnership: `/resources/organizations/transfer-ownership`,
        members: `/resources/organizations/members`,
      },
    },
  },
  sentry: {
    dsn: env.SENTRY_DSN,
  },
  stripe: {
    embedded: true,
    displayMode: StripeCheckoutDisplayMode.Popup,
    products: [
      // {
      //   name: 'Basic',
      //   description: 'A free plan for individuals and teams of 2',
      //   badge: `Free Plan`,
      //   features: [
      //     '1 Workspace',
      //     'Up to 2 members',
      //     'Up to 50,000 tokens per month',
      //   ],
      //   plans: [
      //     {
      //       name: 'Monthly',
      //       price: 'Free',
      //       stripePriceId: env.FREE_PLAN_PRICE_ID,
      //     },
      //     {
      //       name: 'Yearly',
      //       price: 'Free',
      //       stripePriceId: env.FREE_PLAN_PRICE_ID,
      //     },
      //   ],
      // },
      {
        name: 'Basic',
        badge: 'Most Popular',
        recommended: true,
        description: 'For individuals and small groups',
        features: [
          'All our cutting edge features',
          'Up to 10 team members',
          '50,000 tokens per month',
          'Ad-free experience',
          'Search the Internet',
        ],
        plans: [
          {
            name: 'Monthly',
            price: '$14.99',
            stripePriceId: env.TEAM_PLAN_PRICE_ID,
          },
          // {
          //   name: 'Yearly',
          //   price: '$149.99',
          //   stripePriceId: env.TEAM_PLAN_PRICE_ID_YEARLY,
          // },
        ],
      },
      {
        name: 'Ministry',
        badge: '',
        description: 'For communities and larger groups',
        features: [
          'Everything in the Basic Plan',
          'Unlimited team members',
          '200,000 tokens per month',
          'Ad-free experience',
          'Search the Internet',
        ],
        plans: [
          {
            name: 'Monthly',
            price: '$59.99',
            stripePriceId: env.ENTERPRISE_PLAN_PRICE_ID,
          },
          // {
          //   name: 'Yearly',
          //   price: '$599.99',
          //   stripePriceId: env.ENTERPRISE_PLAN_PRICE_ID_YEARLY,
          // },
        ],
      },
      {
        name: 'Custom',
        description: 'Custom plan for your specific needs',
        features: [
          'Everything in the Ministry Plan',
          'Unlimited team members',
          'Ad-free experience',
          'As many tokens as you need',
          'Search the Internet',
        ],
        plans: [
          {
            name: '',
            price: 'Contact us',
            stripePriceId: '',
            label: `Contact us`,
            href: `/contact`,
          },
          // {
          //   name: 'Yearly',
          //   price: '$990',
          //   stripePriceId: 'enterprise-plan-yr',
          // },
        ],
      },
    ],
  },
};

export default configuration;

// Validate Stripe configuration
// as this is a new requirement, we throw an error if the key is not defined
// in the environment
if (
  configuration.stripe.embedded &&
  configuration.production &&
  !env.STRIPE_PUBLISHABLE_KEY
) {
  throw new Error(
    'The key STRIPE_PUBLISHABLE_KEY is not defined. Please add it to your environment variables.',
  );
}

function getBoolean(value: unknown, defaultValue: boolean) {
  if (typeof value === 'string') {
    return value === 'true';
  }

  return defaultValue;
}
